import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import { Trash2, UserPlus } from "lucide-solid";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { Label, TextField } from "@/components/ui/text-field";
import { showToast } from "@/components/ui/toast";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import type { components } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";
import type { Membership } from "./user-detail-view";

type ErrorModel = components["schemas"]["ErrorModel"];
type GuardianLink = components["schemas"]["GuardianLinkDTO"];

/**
 * F2-C — representantes (guardian_link) tab. Guardian links are org-scoped, so we
 * pick one of the user's organizations, then list/add/remove guardians for this
 * user (as the student). CRUD goes through the org handlers (org-admin gated).
 */
export function GuardiansTab(props: { userId: string; memberships: Membership[] }) {
  const { t } = useI18n();

  const orgOptions = createMemo(() =>
    props.memberships.map((m) => ({ value: m.organizationId, label: m.organizationName })),
  );
  // Selected org (empty → default to the user's first membership, tracked).
  const [orgOverride, setOrgOverride] = createSignal<string>("");
  const orgId = () => orgOverride() || props.memberships[0]?.organizationId || "";
  const setOrgId = (v: string) => setOrgOverride(v);
  const [guardianUserId, setGuardianUserId] = createSignal("");
  const [relationship, setRelationship] = createSignal("");

  const guardiansQuery = createApiQuery<GuardianLink[], ErrorModel>(
    () => ["org", "guardians", orgId(), props.userId],
    async () =>
      api.GET("/api/org/listGuardians", {
        params: { query: { organizationId: orgId(), studentUserId: props.userId } },
      }),
    () => ({ enabled: orgId() !== "" }),
  );

  const addMutation = createApiMutation(
    async (vars: {
      organizationId: string;
      guardianUserId: string;
      studentUserId: string;
      relationship?: string;
    }) => api.POST("/api/org/addGuardian", { body: vars }),
  );
  const removeMutation = createApiMutation(async (vars: { id: string }) =>
    api.POST("/api/org/removeGuardian", { body: vars }),
  );

  const add = () => {
    if (!orgId() || !guardianUserId().trim()) {
      return;
    }
    addMutation.mutate(
      {
        organizationId: orgId(),
        guardianUserId: guardianUserId().trim(),
        studentUserId: props.userId,
        relationship: relationship().trim() || undefined,
      },
      {
        onSuccess: () => {
          setGuardianUserId("");
          setRelationship("");
          void guardiansQuery.refetch();
          showToast(t(msg`Guardian linked`), "success");
        },
        onError: () => showToast(t(msg`Could not link guardian`), "error"),
      },
    );
  };

  const remove = (id: string) => {
    removeMutation.mutate(
      { id },
      {
        onSuccess: () => {
          void guardiansQuery.refetch();
          showToast(t(msg`Guardian removed`), "success");
        },
      },
    );
  };

  return (
    <div class="mt-4 space-y-4">
      <Show
        when={props.memberships.length > 0}
        fallback={
          <Card>
            <CardContent class="p-6 text-center text-sm text-muted-foreground">
              {t(msg`Guardians are managed per organization. This user has no memberships.`)}
            </CardContent>
          </Card>
        }
      >
        <div class="w-full sm:w-72">
          <Label>{t(msg`Organization`)}</Label>
          <Select<string>
            aria-label={t(msg`Organization`)}
            value={orgId()}
            options={orgOptions()}
            onChange={(v) => setOrgId(v ?? "")}
          />
        </div>

        <div class="space-y-2">
          <For
            each={guardiansQuery.data ?? []}
            fallback={
              <Card>
                <CardContent class="p-6 text-center text-sm text-muted-foreground">
                  {t(msg`No guardians linked yet.`)}
                </CardContent>
              </Card>
            }
          >
            {(g) => (
              <Card>
                <CardContent class="p-4 flex items-center justify-between gap-3">
                  <div class="min-w-0">
                    <div class="font-medium truncate flex items-center gap-2">
                      {g.guardianName || g.guardianUserId}
                      <Show when={g.isPrimary}>
                        <Badge variant="secondary">{t(msg`Primary`)}</Badge>
                      </Show>
                    </div>
                    <div class="text-xs text-muted-foreground truncate">
                      {g.guardianEmail}
                      <Show when={g.relationship}> · {g.relationship}</Show>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    class="text-destructive hover:text-destructive"
                    onClick={() => remove(g.id)}
                  >
                    <Trash2 class="h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            )}
          </For>
        </div>

        <Card class="bg-primary/5 border-primary/10">
          <CardContent class="p-4 space-y-3">
            <div class="text-sm font-semibold">{t(msg`Link a guardian`)}</div>
            <div class="grid gap-3 sm:grid-cols-2">
              <div>
                <Label for="guardian-id">{t(msg`Guardian user ID`)}</Label>
                <TextField
                  id="guardian-id"
                  value={guardianUserId()}
                  placeholder={t(msg`user id of the guardian`)}
                  onInput={(e) => setGuardianUserId(e.currentTarget.value)}
                />
              </div>
              <div>
                <Label for="guardian-rel">{t(msg`Relationship`)}</Label>
                <TextField
                  id="guardian-rel"
                  value={relationship()}
                  placeholder={t(msg`mother / father / tutor`)}
                  onInput={(e) => setRelationship(e.currentTarget.value)}
                />
              </div>
            </div>
            <Button
              size="sm"
              disabled={addMutation.isPending || !guardianUserId().trim()}
              onClick={add}
            >
              <UserPlus class="mr-1.5 h-4 w-4" />
              {t(msg`Link guardian`)}
            </Button>
          </CardContent>
        </Card>
      </Show>
    </div>
  );
}
